import BrushIcon from '@mui/icons-material/Brush';
import PaletteIcon from '@mui/icons-material/Palette';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Portal from '@mui/material/Portal';
import Button from 'common/components/Elements/Button/Button';
import { defaultColorPickerPalette } from 'common/utilities/colors/colors';
import type { Color, HexColor } from 'common/utilities/colors/colors.types';
import useMouseDown from 'common/utilities/hooks/useMouseDown/useMouseDown';
import { sx } from 'core/utilities/theme';
import React, { useState } from 'react';
import type { RGBColor } from 'react-color';
import { SketchPicker } from 'react-color';
import { useDebouncedCallback } from 'use-debounce';

import classes from './ColorPicker.style';

interface ComponentProps {
  colorPalette?: HexColor[];
  color?: Color;
  /** when missing only show the color within a square (read-only) */
  onChange?: (color: Color) => void;
  notTriggeredFromModal?: boolean;
  open?: boolean;
  onClose?: () => void;
  onClear?: () => void;
  showEditIcon?: boolean;
}

function ColorPicker({
  colorPalette, color, onChange, notTriggeredFromModal, open: openProp, onClose, onClear,
  showEditIcon,
}: ComponentProps) {
  const popperAnchorRef = React.useRef<HTMLButtonElement>(null);
  const isMouseDown = useMouseDown();

  const [open, setOpen] = useState(false);

  const [pickerColor, setPickerColor] = useState<RGBColor | undefined>(
    color ? { r: color[0], g: color[1], b: color[2] } as RGBColor : undefined,
  );

  const debouncedOnChange = useDebouncedCallback((newColor: Color) => {
    if (onChange) onChange(newColor);
  }, 1000);

  return onChange ? (
    <>
      <ButtonBase
        ref={popperAnchorRef}
        sx={sx(
          classes.color,
          color ? { backgroundColor: `rgb(${color.join(',')})`, marginTop: '5px' } : {},
        )}
        onClick={() => setOpen((prevState) => (!prevState))}
      >
        {!color && (
          <PaletteIcon fontSize="small" color="primary" />
        )}
        {color && showEditIcon && (
          <BrushIcon sx={{ color: 'white', fontSize: '13px', mt: '1px' }} />
        )}
      </ButtonBase>
      {(open || openProp) && (
        <>
          {!notTriggeredFromModal && (
            <>
              <Box sx={classes.coverInsideModal} />
              <Portal>
                <Box sx={classes.coverOutsideModal} />
              </Portal>
            </>
          )}
          <ClickAwayListener onClickAway={() => {
            debouncedOnChange.flush();
            if (onClose) onClose();
            setOpen(false);
          }}
          >
            <Popper
              open={open}
              anchorEl={popperAnchorRef.current}
              role={undefined}
              placement="bottom-start"
              sx={classes.popper}
            >
              <SketchPicker
                color={pickerColor}
                presetColors={(colorPalette || defaultColorPickerPalette).map((c) => ({
                  color: c,
                  title: c,
                }))}
                onChange={(newColor) => {
                  setPickerColor(newColor.rgb);
                  if (isMouseDown) {
                    debouncedOnChange([newColor.rgb.r, newColor.rgb.g, newColor.rgb.b]);
                  } else {
                    debouncedOnChange.cancel();
                    onChange([newColor.rgb.r, newColor.rgb.g, newColor.rgb.b]);
                  }
                }}
                styles={{ default: { picker: { boxShadow: '', paddingBottom: onClear ? '50px' : undefined } } }}
                disableAlpha
              />
              {onClear && (
                <Box sx={{ ml: 1.5, mr: 1.5, mt: '-50px' }}>
                  <Button
                    action="secondary"
                    onClick={() => onClear()}
                    fullWidth
                  >
                    Clear color
                  </Button>
                </Box>
              )}
            </Popper>
          </ClickAwayListener>
        </>
      )}
    </>
  ) : (
    <Box
      component="span"
      sx={sx(
        classes.color,
        color ? { backgroundColor: `rgb(${color.join(',')})` } : {},
      )}
    />
  );
}

export default ColorPicker;
